import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {Dispatch} from "redux";
import {api} from "../../../api/api";
import CustomField from "../../../model/customField/CustomField";
import {
	JOB_CUSTOM_FIELD_CREATED,
	JOB_CUSTOM_FIELD_UPDATED,
	JOB_CUSTOM_FIELDS_FETCHED,
	LEAD_CUSTOM_FIELD_CREATED,
	LEAD_CUSTOM_FIELD_UPDATED,
	LEAD_CUSTOM_FIELDS_FETCHED,
	PARTNER_BRAND_CUSTOM_FIELD_CREATED,
	PARTNER_BRAND_CUSTOM_FIELD_UPDATED,
	PARTNER_BRAND_CUSTOM_FIELDS_FETCHED
} from "./actionType";
import {Action} from "redux-actions";

/**
 * Job custom fields fetched successful action.
 * @param {CustomField[]} fields
 * @returns {{type: string | JOB_CUSTOM_FIELDS_FETCHED; payload: {fields: CustomField[]}}}
 */
export function jobCustomFieldsFetched(fields: CustomField[]) {
	return {type: JOB_CUSTOM_FIELDS_FETCHED, payload: {fields}}
}

/**
 * Lead custom fields fetched successful action.
 * @param {CustomField[]} fields
 * @returns {{type: string | LEAD_CUSTOM_FIELDS_FETCHED; payload: {fields: CustomField[]}}}
 */
export function leadCustomFieldsFetched(fields: CustomField[]): Action<LEAD_CUSTOM_FIELDS_FETCHED> {
	return {type: LEAD_CUSTOM_FIELDS_FETCHED, payload: {fields}}
}

/**
 * Partner brand custom fields fetched successful action.
 * @param {CustomField[]} fields
 * @returns {{type: string | PARTNER_BRAND_CUSTOM_FIELDS_FETCHED; payload: {fields: CustomField[]}}}
 */
export function partnerBrandCustomFieldsFetched(fields: CustomField[]): Action<PARTNER_BRAND_CUSTOM_FIELDS_FETCHED> {
	return {type: PARTNER_BRAND_CUSTOM_FIELDS_FETCHED, payload: {fields}}
}

/**
 * Job custom field created successful action.
 * @param {CustomField} field
 * @returns {Action<JOB_CUSTOM_FIELD_CREATED>}
 */
function jobCustomFieldCreated(field: CustomField): Action<JOB_CUSTOM_FIELD_CREATED> {
	return {type: JOB_CUSTOM_FIELD_CREATED, payload: {field}};
}

/**
 * Lead custom field created successful action.
 * @param {CustomField} field
 * @returns {Action<LEAD_CUSTOM_FIELD_CREATED>}
 */
function leadCustomFieldCreated(field: CustomField): Action<LEAD_CUSTOM_FIELD_CREATED> {
	return {type: LEAD_CUSTOM_FIELD_CREATED, payload: {field}};
}

/**
 * Partner Brand custom field created successful action.
 * @param {CustomField} field
 * @returns {Action<PARTNER_BRAND_CUSTOM_FIELD_CREATED>}
 */
function partnerBrandCustomFieldCreated(field: CustomField): Action<PARTNER_BRAND_CUSTOM_FIELD_CREATED> {
	return {type: PARTNER_BRAND_CUSTOM_FIELD_CREATED, payload: {field}};
}

/**
 * Job custom field updated successful action.
 * @param {CustomField} field
 * @returns {Action<JOB_CUSTOM_FIELD_UPDATED>}
 */
function jobCustomFieldUpdated(field: CustomField): Action<JOB_CUSTOM_FIELD_UPDATED> {
	return {type: JOB_CUSTOM_FIELD_UPDATED, payload: {field}};
}

/**
 * Lead custom field updated successful action.
 * @param {CustomField} field
 * @returns {Action<LEAD_CUSTOM_FIELD_UPDATED>}
 */
function leadCustomFieldUpdated(field: CustomField): Action<LEAD_CUSTOM_FIELD_UPDATED> {
	return {type: LEAD_CUSTOM_FIELD_UPDATED, payload: {field}};
}

/**
 * Partner Brand custom field updated successful action.
 * @param {CustomField} field
 * @returns {Action<PARTNER_BRAND_CUSTOM_FIELD_UPDATED>}
 */
function partnerBrandCustomFieldUpdated(field: CustomField): Action<PARTNER_BRAND_CUSTOM_FIELD_UPDATED> {
	return {type: PARTNER_BRAND_CUSTOM_FIELD_UPDATED, payload: {field}};
}

/**
 * Create job custom field
 * @param {CustomField} customField
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function createJobCustomField(customField: CustomField, onSuccess: OnSuccessData<CustomField>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.customFieldApi.createJobCustomField(customField.toJson()).then((customField) => {
			dispatch(jobCustomFieldCreated(customField));
			onSuccess(customField);
		}).catch(onFailure);
	};
}

/**
 * Create lead custom field
 * @param {CustomField} customField
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function createLeadCustomField(customField: CustomField, onSuccess: OnSuccessData<CustomField>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.customFieldApi.createLeadCustomField(customField.toJson()).then((customField) => {
			dispatch(leadCustomFieldCreated(customField));
			onSuccess(customField);
		}).catch(onFailure);
	};
}

/**
 * Create partner brand custom field
 * @param {CustomField} customField
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function createPartnerBrandCustomField(customField: CustomField,
	onSuccess: OnSuccessData<CustomField>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.customFieldApi.createPartnerBrandCustomField(customField.toJson()).then((customField) => {
			dispatch(partnerBrandCustomFieldCreated(customField));
			onSuccess(customField);
		}).catch(onFailure);
	};
}

/**
 * Update job custom field
 * @param {CustomField} customField
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function updateJobCustomField(customField: CustomField, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.customFieldApi.updateJobCustomField(customField.id, customField.toJson()).then(() => {
			dispatch(jobCustomFieldUpdated(customField));
			onSuccess();
		}).catch(onFailure);
	};
}

/**
 * Update lead custom field
 * @param {CustomField} customField
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function updateLeadCustomField(customField: CustomField, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.customFieldApi.updateLeadCustomField(customField.id, customField.toJson()).then(() => {
			dispatch(leadCustomFieldUpdated(customField));
			onSuccess();
		}).catch(onFailure);
	};
}

/**
 * Update partner brand custom field
 * @param {CustomField} customField
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function updatePartnerBrandCustomField(customField: CustomField, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.customFieldApi.updatePartnerBrandCustomField(customField.id, customField.toJson()).then(() => {
			dispatch(partnerBrandCustomFieldUpdated(customField));
			onSuccess();
		}).catch(onFailure);
	};
}

/**
 * Fetch job custom fields from server.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchJobCustomFields(onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.customFieldApi.fetchJobCustomFields().then(fields => {
			dispatch(jobCustomFieldsFetched(fields));
			onSuccess();
		}).catch(onFailure);
	};
}

/**
 * Fetch lead custom fields from server.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchLeadCustomFields(onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.customFieldApi.fetchLeadCustomFields().then(fields => {
			dispatch(leadCustomFieldsFetched(fields));
			onSuccess();
		}).catch(onFailure);
	};
}

/**
 * Fetch Partner Brand custom fields from server.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchPartnerBrandCustomFields(onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.customFieldApi.fetchPartnerBrandCustomFields().then(fields => {
			dispatch(partnerBrandCustomFieldsFetched(fields));
			onSuccess();
		}).catch(onFailure);
	};
}